import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Chip,
  Box,
  Divider,
} from '@mui/material'
import { formatDistanceToNow } from 'date-fns'
import { WhatsAppStatusData } from './WhatsAppStatusCard'

interface WhatsAppDetailsDialogProps {
  open: boolean
  onClose: () => void
  status?: WhatsAppStatusData | null
}

export function WhatsAppDetailsDialog({ open, onClose, status }: WhatsAppDetailsDialogProps) {
  if (!status) {
    return null
  }

  const {
    status: credentialStatus,
    health_check_status: healthStatus,
    is_verified: isVerified,
    is_healthy: isHealthy,
    verified_at: verifiedAt,
    last_health_check: lastChecked,
    business_name: businessName,
    phone_number: phoneNumber,
    display_phone_number: displayPhone,
    business_account_id: businessAccountId,
    phone_number_id: phoneNumberId,
    app_id: appId,
    smb_name: smbName,
    api_calls_today: apiCallsToday,
    api_calls_this_month: apiCallsMonth,
    messaging_limit: messagingLimit,
    quality_score: qualityScore,
    health_check_message: healthMessage,
    last_error_at: lastErrorAt,
    last_error_message: lastErrorMessage,
    consecutive_failures: consecutiveFailures,
  } = status

  const safeFormattedDate = (date?: string) => {
    if (!date) return 'Never'
    try {
      return `${new Date(date).toLocaleString()} (${formatDistanceToNow(new Date(date))} ago)`
    } catch (error) {
      return new Date(date).toLocaleString()
    }
  }

  const infoRows: Array<{ label: string; value: string | number | null }> = [
    { label: 'Business Name', value: businessName || smbName || 'Unknown' },
    { label: 'Phone Number', value: displayPhone || phoneNumber || 'Unknown' },
    { label: 'Credential Status', value: credentialStatus },
    { label: 'Business Account ID', value: businessAccountId || 'Unknown' },
    { label: 'Phone Number ID', value: phoneNumberId || 'Unknown' },
    { label: 'App ID', value: appId || 'Unknown' },
    { label: 'Health Status', value: healthStatus || (isHealthy ? 'healthy' : 'unknown') },
    { label: 'Verified', value: isVerified ? 'Yes' : 'No' },
    { label: 'Last Verified', value: safeFormattedDate(verifiedAt) },
    { label: 'Last Health Check', value: safeFormattedDate(lastChecked) },
    { label: 'Health Message', value: healthMessage || '—' },
    { label: "Today's API Calls", value: apiCallsToday },
    { label: "Monthly API Calls", value: apiCallsMonth },
    { label: 'Messaging Limit', value: messagingLimit || 'Not provided' },
    { label: 'Quality Score', value: qualityScore || 'Unknown' },
    { label: 'Consecutive Failures', value: consecutiveFailures || '0' },
    { label: 'Last Error', value: lastErrorMessage || 'None' },
    { label: 'Last Error Time', value: safeFormattedDate(lastErrorAt) },
  ]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>WhatsApp Integration Details</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Overall Status
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Credentials: ${credentialStatus}`} color={isVerified ? 'success' : 'warning'} variant="outlined" />
            <Chip label={`Health: ${healthStatus || 'unknown'}`} color={isHealthy ? 'success' : 'warning'} variant="outlined" />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          {infoRows.map(({ label, value }) => (
            <Grid item xs={12} sm={6} key={label}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {value ?? '—'}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
